import React, { FormEvent, PointerEvent as ReactPointerEvent, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { isPanelDragHandleTarget, movePanelByDelta } from './floating-panel-drag';
import { FaPaperPlane } from '@react-icons/all-files/fa/FaPaperPlane';
import { FaTimes } from '@react-icons/all-files/fa/FaTimes';
import {
  assistantReplyHtml,
  clearChatSession,
  ensureChatSession,
  loadChatSession,
  OPEN_SUPERCONTA_CHAT_EVENT,
  ADD_SUPERCONTA_CHAT_MESSAGE_EVENT,
  addMessageToChat,
  consumeChatMsgToFromWindow,
  parseSuperContaStatus,
  runChatActions,
  sendChatMessage,
  whenPageReady,
  type ActionRunner,
  type ChatMessage,
  type ChatPoster,
  type StorageLike,
  type SuperContaChatSession,
} from './superconta-chat';
import { periodoSeleccionar } from '../shared/workflow-periodo-config';
import { titilarKanban } from '../shared/titilar-kanban';
import { titilarTarea } from '../shared/titilar-tarea';
import { moverTarea } from '../shared/mover-tarea';
import { addWorkflowEdge } from '../shared/add-workflow-edge';
import { applyWorkflowLayout, pedirMejoraLayout } from '../shared/apply-workflow-layout';
import { playSound } from '../shared/audio/audio';

const SUPERCONTA_AVATAR = '/assets/images/superconta-bot.png';
const SUPERCONTA_OPEN_SOUND = '/assets/audio/robot.wav';
const SUPERCONTA_STATUS_URL = '/api/super-conta/status';
const STATUS_POLL_MS = 15000;

export async function defaultChatPoster(url: string, body?: unknown): Promise<unknown> {
  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body ?? {}),
  });
  const text = await response.text();
  if (!response.ok) {
    throw new Error(text.trim() || `Error ${response.status}`);
  }
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export async function defaultStatusFetcher(): Promise<{ ok: boolean }> {
  try {
    const response = await fetch(SUPERCONTA_STATUS_URL, { credentials: 'include' });
    const text = await response.text();
    if (!response.ok) return { ok: false };
    return { ok: parseSuperContaStatus(text ? JSON.parse(text) : null) };
  } catch {
    return { ok: false };
  }
}

interface SuperContaChatProps {
  poster?: ChatPoster;
  storage?: StorageLike | null;
  statusFetcher?: () => Promise<{ ok?: boolean } | null>;
  runAction?: ActionRunner;
}

function userMessageHtml(content: string): string | null {
  return content.includes('<a ') ? content : null;
}

function MessageBubble({ message }: { message: ChatMessage }) {
  const html =
    message.role === 'assistant' ? assistantReplyHtml(message.content) : userMessageHtml(message.content);
  return (
    <div className={`crm-superconta-bubble crm-superconta-bubble--${message.role}`}>
      {html ? (
        <div className="crm-superconta-html" dangerouslySetInnerHTML={{ __html: html }} />
      ) : (
        message.content
      )}
    </div>
  );
}

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof Error && err.message.trim()) return err.message;
  return fallback;
}

export default function SuperContaChat({
  poster = defaultChatPoster,
  storage,
  statusFetcher = defaultStatusFetcher,
  runAction,
}: SuperContaChatProps) {
  const [open, setOpen] = useState(false);
  const [online, setOnline] = useState(false);
  const [session, setSession] = useState<SuperContaChatSession | null>(() => loadChatSession(storage));
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [waitingReply, setWaitingReply] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null);
  const [dragging, setDragging] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{
    pointerId?: number;
    startX: number;
    startY: number;
    originX: number;
    originY: number;
  } | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const sendButtonRef = useRef<HTMLButtonElement>(null);
  const busyRef = useRef(false);
  const pendingSendRef = useRef<string | null>(null);
  const sendUserTextRef = useRef<(text: string) => Promise<void>>(async () => undefined);

  busyRef.current = busy;
  sendUserTextRef.current = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || busyRef.current) return;
    busyRef.current = true;
    setOpen(true);
    setBusy(true);
    setWaitingReply(true);
    setError(null);
    setDraft('');
    setSession(current => ({
      uuid: current?.uuid ?? '',
      messages: [...(current?.messages ?? []), { role: 'user', content: trimmed }],
    }));
    try {
      const result = await sendChatMessage(poster, trimmed, storage);
      setSession(result.session);
      runChatActions(result.actions, runAction);
    } catch (err) {
      setError(errorMessage(err, 'No se pudo enviar el mensaje'));
      setDraft(current => current || trimmed);
      setSession(loadChatSession(storage));
    } finally {
      busyRef.current = false;
      setWaitingReply(false);
      setBusy(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      try {
        const payload = await statusFetcher();
        if (!cancelled) setOnline(parseSuperContaStatus(payload));
      } catch {
        if (!cancelled) setOnline(false);
      }
    };
    refresh();
    const timer = window.setInterval(refresh, STATUS_POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [statusFetcher]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setError(null);
    ensureChatSession(poster, storage)
      .then(next => {
        if (cancelled) return;
        setSession(current => {
          if (current?.messages.length && current.messages.length > next.messages.length) {
            return { uuid: next.uuid || current.uuid, messages: current.messages };
          }
          return next;
        });
      })
      .catch(err => {
        if (!cancelled) setError(errorMessage(err, 'No se pudo abrir el chat'));
      });
    return () => {
      cancelled = true;
    };
  }, [open, poster, storage]);

  useEffect(() => {
    if (!open) return;
    const list = listRef.current;
    if (list) list.scrollTop = list.scrollHeight;
    inputRef.current?.focus();
  }, [open, session?.messages.length]);

  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener(OPEN_SUPERCONTA_CHAT_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_SUPERCONTA_CHAT_EVENT, onOpen);
  }, []);

  useEffect(() => {
    window.addMessageToChat = addMessageToChat;
    window.periodoSeleccionar = periodoSeleccionar;
    window.titilarKanban = titilarKanban;
    window.titilarTarea = titilarTarea;
    window.moverTarea = moverTarea;
    window.addEdge = addWorkflowEdge;
    window.applyWorkflowLayout = applyWorkflowLayout;
    window.pedirMejoraLayout = pedirMejoraLayout;
    const onAdd = (event: Event) => {
      const text = (event as CustomEvent<{ text?: string }>).detail?.text?.trim();
      if (!text) return;
      pendingSendRef.current = text;
      setOpen(true);
      setDraft(text);
    };
    window.addEventListener(ADD_SUPERCONTA_CHAT_MESSAGE_EVENT, onAdd);
    return () => window.removeEventListener(ADD_SUPERCONTA_CHAT_MESSAGE_EVENT, onAdd);
  }, []);

  useEffect(() => {
    const pending = pendingSendRef.current;
    if (!open || busy || !pending || draft !== pending) return;
    const sendButton = sendButtonRef.current;
    if (!sendButton || sendButton.disabled) return;
    pendingSendRef.current = null;
    sendButton.click();
  }, [open, draft, busy]);

  useEffect(() => {
    let cancelReady: () => void = () => undefined;
    const consume = () => {
      const text = consumeChatMsgToFromWindow();
      if (!text) return;
      cancelReady();
      cancelReady = whenPageReady(() => addMessageToChat(text));
    };
    consume();
    window.addEventListener('hashchange', consume);
    return () => {
      window.removeEventListener('hashchange', consume);
      cancelReady();
    };
  }, []);

  useEffect(() => {
    if (!position) return undefined;
    const onResize = () => {
      const panel = panelRef.current;
      if (!panel) return;
      const rect = panel.getBoundingClientRect();
      setPosition(current =>
        current
          ? movePanelByDelta(
              current,
              { x: 0, y: 0 },
              { width: rect.width, height: rect.height },
              { width: window.innerWidth, height: window.innerHeight },
            )
          : current,
      );
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [position]);

  const applyPanelDrag = (clientX: number, clientY: number, pointerId?: number) => {
    const drag = dragRef.current;
    const panel = panelRef.current;
    if (!drag || !panel) return;
    if (!Number.isFinite(clientX) || !Number.isFinite(clientY)) return;
    if (drag.pointerId != null && pointerId != null && drag.pointerId !== pointerId) return;
    const rect = panel.getBoundingClientRect();
    setPosition(
      movePanelByDelta(
        { x: drag.originX, y: drag.originY },
        { x: clientX - drag.startX, y: clientY - drag.startY },
        { width: rect.width, height: rect.height },
        { width: window.innerWidth, height: window.innerHeight },
      ),
    );
  };

  useEffect(() => {
    if (!dragging) return undefined;
    const samePointer = (event: PointerEvent) =>
      dragRef.current?.pointerId == null ||
      event.pointerId == null ||
      dragRef.current.pointerId === event.pointerId;

    const onMove = (event: PointerEvent) => {
      applyPanelDrag(event.clientX, event.clientY, event.pointerId);
    };
    const onUp = (event: PointerEvent) => {
      if (!samePointer(event)) return;
      dragRef.current = null;
      setDragging(false);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [dragging]);

  const beginPanelDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button != null && event.button !== 0) return;
    if (!isPanelDragHandleTarget(event.target, headerRef.current)) return;
    const panel = panelRef.current;
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: rect.left,
      originY: rect.top,
    };
    setPosition({ x: rect.left, y: rect.top });
    setDragging(true);
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const movePanelDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    applyPanelDrag(event.clientX, event.clientY, event.pointerId);
  };

  const endPanelDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (dragRef.current?.pointerId != null && event.pointerId != null && dragRef.current.pointerId !== event.pointerId) {
      return;
    }
    dragRef.current = null;
    setDragging(false);
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    await sendUserTextRef.current(inputRef.current?.value || draft);
  };

  const handleClear = async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError(null);
    setDraft('');
    clearChatSession(storage);
    setSession(null);
    try {
      const next = await ensureChatSession(poster, storage);
      setSession(next);
    } catch (err) {
      setError(errorMessage(err, 'No se pudo limpiar el chat'));
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  return (
    <div className="crm-superconta-chat">
      <button
        type="button"
        className={`crm-icon-btn${open ? ' is-active' : ''}`}
        onClick={() =>
          setOpen(value => {
            const next = !value;
            if (next) playSound(SUPERCONTA_OPEN_SOUND);
            return next;
          })
        }
        title="Chat con Super Conta"
        aria-label="Chat con Super Conta"
        aria-expanded={open}
      >
        <img src={SUPERCONTA_AVATAR} alt="" className="crm-superconta-launcher-icon" />
        <span
          role="status"
          className={`crm-superconta-status${online ? ' is-online' : ''}`}
          aria-label={online ? 'Super Conta online' : 'Super Conta offline'}
          title={online ? 'Super Conta online' : 'Super Conta offline'}
        />
      </button>

      {open &&
        createPortal(
          <div
            ref={panelRef}
            className={`crm-superconta-panel${dragging ? ' is-dragging' : ''}`}
            role="dialog"
            aria-label="Chat con Super Conta"
            style={
              position
                ? { left: position.x, top: position.y, right: 'auto' }
                : undefined
            }
          >
            <div
              ref={headerRef}
              className="crm-superconta-panel-header"
              onPointerDown={beginPanelDrag}
              onPointerMove={movePanelDrag}
              onPointerUp={endPanelDrag}
              onPointerCancel={endPanelDrag}
            >
              <img src={SUPERCONTA_AVATAR} alt="" className="crm-superconta-avatar" draggable={false} />
              <div className="crm-superconta-heading">
                <strong>Super Conta</strong>
                <span>{online ? 'Online' : 'Offline'}</span>
              </div>
              <button
                type="button"
                className="crm-superconta-clear"
                onClick={handleClear}
                disabled={busy}
                aria-label="Limpiar"
                title="Limpiar conversación"
              >
                Limpiar
              </button>
              <button
                type="button"
                className="crm-icon-btn"
                onClick={() => setOpen(false)}
                aria-label="Cerrar chat"
                title="Cerrar chat"
              >
                <FaTimes />
              </button>
            </div>

            <div className="crm-superconta-messages" ref={listRef}>
              {session?.messages.length ? (
                session.messages.map((message, index) => (
                  <MessageBubble key={`${message.role}-${index}`} message={message} />
                ))
              ) : (
                <p className="crm-superconta-empty">Escribe para hablar con Super Conta.</p>
              )}
            </div>

            {error && <p className="crm-superconta-error">{error}</p>}

            <div className="crm-superconta-composer-wrap">
              <form className="crm-superconta-composer" onSubmit={handleSubmit}>
                <input
                  ref={inputRef}
                  type="text"
                  className="crm-superconta-input"
                  placeholder="Escribe un mensaje…"
                  value={draft}
                  onChange={event => setDraft(event.target.value)}
                  disabled={busy}
                  autoComplete="off"
                />
                <button
                  ref={sendButtonRef}
                  type="submit"
                  className="crm-superconta-send"
                  aria-label="Enviar"
                  disabled={busy || !draft.trim()}
                >
                  <FaPaperPlane />
                </button>
              </form>
              {waitingReply && (
                <p className="crm-superconta-typing" role="status">
                  Super Conta está escribiendo ...
                </p>
              )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
